import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import FileCard from './components/FileCard';
import FolderCard from './components/FolderCard';
import FileUploadZone from './components/FileUploadZone';
import FilePreviewModal from './components/FilePreviewModal';
import BulkActionsBar from './components/BulkActionsBar';
import SearchAndFilters from './components/SearchAndFilters';

const DigitalVault = () => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(['Vault']);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Mock data for folders
  const mockFolders = [
    {
      id: 'folder-1',
      name: 'Diamond Ring Collection',
      fileCount: 24,
      lastModified: new Date('2024-12-10'),
      description: 'High-end diamond engagement rings and wedding bands'
    },
    {
      id: 'folder-2',
      name: 'Custom Necklaces',
      fileCount: 18,
      lastModified: new Date('2024-12-08'),
      description: 'Bespoke necklace designs for premium clients'
    },
    {
      id: 'folder-3',
      name: 'CAD Drawings',
      fileCount: 42,
      lastModified: new Date('2024-12-05'),
      description: 'Technical drawings and 3D models'
    },
    {
      id: 'folder-4',
      name: 'Client Presentations',
      fileCount: 15,
      lastModified: new Date('2024-12-03'),
      description: 'Marketing materials and client proposals'
    }
  ];

  // Mock data for files
  const mockFiles = [
    {
      id: 'file-1',
      name: 'Diamond_Solitaire_Render_v3.jpg',
      type: 'image',
      size: 2457600,
      uploadDate: new Date('2024-12-10T14:30:00'),
      project: 'Diamond Ring Collection',
      thumbnail: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=300&h=300&fit=crop',
      url: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=800&h=800&fit=crop',
      tags: ['diamond', 'solitaire', 'engagement']
    },
    {
      id: 'file-2',
      name: 'Wedding_Band_Animation.mp4',
      type: 'video',
      size: 15728640,
      uploadDate: new Date('2024-12-09T16:45:00'),
      project: 'Wedding Bands 2024',
      thumbnail: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=300&h=300&fit=crop',
      url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4'
    },
    {
      id: 'file-3',
      name: 'Emerald_Necklace_CAD.dwg',
      type: 'cad',
      size: 5242880,
      uploadDate: new Date('2024-12-08T11:20:00'),
      project: 'Custom Necklaces',
      tags: ['emerald', 'necklace', 'cad']
    },
    {
      id: 'file-4',
      name: 'Vintage_Brooch_Restoration.jpg',
      type: 'image',
      size: 3145728,
      uploadDate: new Date('2024-12-07T09:15:00'),
      project: 'Vintage Restoration',
      thumbnail: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=300&h=300&fit=crop',
      url: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&h=800&fit=crop',
      tags: ['vintage', 'brooch', 'restoration']
    },
    {
      id: 'file-5',
      name: 'Pearl_Earrings_Showcase.jpg',
      type: 'image',
      size: 1835008,
      uploadDate: new Date('2024-12-06T13:30:00'),
      project: 'Pearl Collection',
      thumbnail: 'https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=300&h=300&fit=crop',
      url: 'https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=800&h=800&fit=crop',
      tags: ['pearl', 'earrings', 'showcase']
    },
    {
      id: 'file-6',
      name: 'Sapphire_Ring_Technical.pdf',
      type: 'document',
      size: 1048576,
      uploadDate: new Date('2024-12-05T10:45:00'),
      project: 'Sapphire Collection',
      tags: ['sapphire', 'technical', 'specifications']
    }
  ];

  const [files, setFiles] = useState(mockFiles);
  const [folders, setFolders] = useState(mockFolders);

  // Storage statistics - Updated to 10GB limit
  const totalFiles = files?.length;
  const totalSize = files?.reduce((sum, file) => sum + file?.size, 0);
  const storageUsed = (totalSize / (1024 * 1024 * 1024))?.toFixed(2); // GB
  const storageLimit = 10; // Changed from 100GB to 10GB
  const storagePercentage = (storageUsed / storageLimit) * 100;
  const isStorageNearLimit = storagePercentage > 80;
  const isStorageAtLimit = storagePercentage >= 100;

  const handleFileSelect = (fileId) => {
    setSelectedFiles(prev => 
      prev?.includes(fileId) 
        ? prev?.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles?.length === files?.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files?.map(file => file?.id));
    }
  };

  const handleFileUpload = (uploadedFiles) => {
    // Calculate total size of new files
    const newFilesSize = uploadedFiles?.reduce((sum, file) => sum + file?.size, 0);
    const newTotalSize = totalSize + newFilesSize;
    const newStorageUsed = newTotalSize / (1024 * 1024 * 1024);
    
    // Check if upload would exceed storage limit
    if (newStorageUsed > storageLimit) {
      const exceededBy = (newStorageUsed - storageLimit)?.toFixed(2);
      alert(`Upload failed: This would exceed your storage limit by ${exceededBy} GB. Please free up space or upgrade your plan.`);
      return;
    }

    // Check if upload would bring storage near limit (warning)
    const newStoragePercentage = (newStorageUsed / storageLimit) * 100;
    if (newStoragePercentage > 90 && storagePercentage <= 90) {
      if (!window.confirm(`Warning: This upload will bring your storage to ${newStoragePercentage?.toFixed(1)}% of your ${storageLimit}GB limit. Continue?`)) {
        return;
      }
    }

    setIsUploading(true);
    
    // Simulate file upload process
    setTimeout(() => {
      const newFiles = uploadedFiles?.map((file, index) => ({
        id: `file-${Date.now()}-${index}`,
        name: file?.name,
        type: file?.type?.startsWith('image/') ? 'image' : 
              file?.type?.startsWith('video/') ? 'video' : 'document',
        size: file?.size,
        uploadDate: new Date(),
        project: null,
        thumbnail: file?.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
        url: URL.createObjectURL(file)
      }));
      
      setFiles(prev => [...newFiles, ...prev]);
      setIsUploading(false);
    }, 2000);
  };

  const handleFilePreview = (file) => {
    setPreviewFile(file);
    setIsPreviewOpen(true);
  };

  const handleFileDownload = (file) => {
    // Simulate file download
    const link = document.createElement('a');
    link.href = file?.url || '#';
    link.download = file?.name;
    link?.click();
  };

  const handleFileDelete = (file) => {
    if (window.confirm(`Are you sure you want to delete "${file?.name}"?`)) {
      setFiles(prev => prev?.filter(f => f?.id !== file?.id));
      setSelectedFiles(prev => prev?.filter(id => id !== file?.id));
      setIsPreviewOpen(false);
    }
  };

  const handleBulkDownload = () => {
    const selectedFileObjects = files?.filter(file => selectedFiles?.includes(file?.id));
    selectedFileObjects?.forEach(file => handleFileDownload(file));
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedFiles?.length} files?`)) {
      setFiles(prev => prev?.filter(file => !selectedFiles?.includes(file?.id)));
      setSelectedFiles([]);
    }
  };

  const handleBulkMove = () => {
    // Implement bulk move functionality
    console.log('Moving files:', selectedFiles);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
  };

  const handleFolderOpen = (folder) => {
    setCurrentPath(prev => [...prev, folder?.name]);
  };

  const handleFolderRename = (folder) => {
    const newName = prompt('Enter new folder name:', folder?.name);
    if (newName && newName !== folder?.name) {
      setFolders(prev => prev?.map(f => 
        f?.id === folder?.id ? { ...f, name: newName } : f
      ));
    }
  };

  const handleFolderDelete = (folder) => {
    if (window.confirm(`Are you sure you want to delete "${folder?.name}" and all its contents?`)) {
      setFolders(prev => prev?.filter(f => f?.id !== folder?.id));
    }
  };

  const handleCreateFolder = () => {
    if (!newFolderName?.trim()) {
      alert('Please enter a folder name');
      return;
    }

    // Check if folder name already exists
    const folderExists = folders?.some(folder => 
      folder?.name?.toLowerCase() === newFolderName?.trim()?.toLowerCase()
    );

    if (folderExists) {
      alert('A folder with this name already exists');
      return;
    }

    // Create new folder
    const newFolder = {
      id: `folder-${Date.now()}`,
      name: newFolderName?.trim(),
      fileCount: 0,
      lastModified: new Date(),
      description: `New folder created on ${new Date()?.toLocaleDateString()}`
    };

    setFolders(prev => [newFolder, ...prev]);
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  const handleCancelCreateFolder = () => {
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  const handleNewFolderClick = () => {
    setIsCreatingFolder(true);
  };

  const filteredFiles = files?.filter(file => {
    if (searchQuery && !file?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
      return false;
    }
    if (filters?.type && filters?.type !== 'all' && file?.type !== filters?.type) {
      return false;
    }
    if (filters?.project && filters?.project !== 'all' && file?.project !== filters?.project) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-heading text-foreground">Vault</h1>
              <p className="text-muted-foreground mt-1">
                Secure storage for your jewelry design assets
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" asChild>
                <Link to="/create-project">
                  <Icon name="Plus" size={16} />
                  New Project
                </Link>
              </Button>
              <Button variant="outline" onClick={handleNewFolderClick}>
                <Icon name="FolderPlus" size={16} />
                New Folder
              </Button>
            </div>
          </div>

          {/* Create Folder Modal/Input */}
          {isCreatingFolder && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading text-foreground">Create New Folder</h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCancelCreateFolder}
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Folder Name
                    </label>
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter') {
                          handleCreateFolder();
                        } else if (e?.key === 'Escape') {
                          handleCancelCreateFolder();
                        }
                      }}
                      placeholder="Enter folder name..."
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                      autoFocus
                    />
                  </div>
                  
                  <div className="flex items-center justify-end space-x-3">
                    <Button 
                      variant="outline" 
                      onClick={handleCancelCreateFolder}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateFolder}
                      disabled={!newFolderName?.trim()}
                    >
                      <Icon name="FolderPlus" size={16} />
                      Create Folder
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Storage Limit Warning Banner */}
          {isStorageNearLimit && (
            <div className={`rounded-lg p-4 mb-6 border ${
              isStorageAtLimit 
                ? 'bg-destructive/5 border-destructive/20' :'bg-warning/5 border-warning/20'
            }`}>
              <div className="flex items-center space-x-3">
                <Icon 
                  name={isStorageAtLimit ? 'AlertTriangle' : 'AlertCircle'} 
                  size={20} 
                  color={isStorageAtLimit ? 'var(--color-destructive)' : 'var(--color-warning)'} 
                />
                <div>
                  <p className={`font-medium ${
                    isStorageAtLimit ? 'text-destructive' : 'text-warning'
                  }`}>
                    {isStorageAtLimit ? 'Storage Limit Reached' : 'Storage Nearly Full'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isStorageAtLimit 
                      ? 'You cannot upload new files until you free up space or upgrade your plan.' : `You're using ${storagePercentage?.toFixed(1)}% of your ${storageLimit}GB storage limit.`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Storage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Files" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-2xl font-heading text-foreground">{totalFiles}</p>
                  <p className="text-sm text-muted-foreground">Total Files</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isStorageAtLimit 
                    ? 'bg-destructive/10' 
                    : isStorageNearLimit 
                      ? 'bg-warning/10' :'bg-success/10'
                }`}>
                  <Icon 
                    name="HardDrive" 
                    size={20} 
                    color={isStorageAtLimit 
                      ? 'var(--color-destructive)' 
                      : isStorageNearLimit 
                        ? 'var(--color-warning)' 
                        : 'var(--color-success)'
                    } 
                  />
                </div>
                <div>
                  <p className="text-2xl font-heading text-foreground">{storageUsed} GB</p>
                  <p className="text-sm text-muted-foreground">Storage Used</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Folder" size={20} color="var(--color-warning)" />
                </div>
                <div>
                  <p className="text-2xl font-heading text-foreground">{folders?.length}</p>
                  <p className="text-sm text-muted-foreground">Folders</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Storage</span>
                  <span className={`text-sm ${
                    isStorageAtLimit 
                      ? 'text-destructive font-medium' 
                      : isStorageNearLimit 
                        ? 'text-warning font-medium' :'text-muted-foreground'
                  }`}>
                    {storageUsed}/{storageLimit} GB
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      isStorageAtLimit 
                        ? 'bg-destructive' 
                        : isStorageNearLimit 
                          ? 'bg-warning' :'bg-accent'
                    }`}
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={`${
                    isStorageAtLimit 
                      ? 'text-destructive' 
                      : isStorageNearLimit 
                        ? 'text-warning' :'text-muted-foreground'
                  }`}>
                    {storagePercentage?.toFixed(1)}% used
                  </span>
                  <span className="text-muted-foreground">
                    {(storageLimit - parseFloat(storageUsed))?.toFixed(2)} GB free
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <SearchAndFilters
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
            />
          </div>

          {/* View Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Icon name="Grid3X3" size={16} />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <Icon name="List" size={16} />
                </Button>
              </div>

              {files?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  <Icon name={selectedFiles?.length === files?.length ? 'CheckSquare' : 'Square'} size={16} />
                  {selectedFiles?.length === files?.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>

            <div className="text-sm text-muted-foreground">
              {filteredFiles?.length} of {files?.length} files
            </div>
          </div>

          {/* Upload Zone */}
          <div className="mb-8">
            <FileUploadZone
              onFileUpload={handleFileUpload}
              isUploading={isUploading}
              storageUsed={parseFloat(storageUsed)}
              storageLimit={storageLimit}
              storagePercentage={storagePercentage}
              isStorageAtLimit={isStorageAtLimit}
            />
          </div>

          {/* Folders Section */}
          {folders?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-heading text-foreground mb-4">Folders</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {folders?.map((folder) => (
                  <FolderCard
                    key={folder?.id}
                    folder={folder}
                    onOpen={handleFolderOpen}
                    onRename={handleFolderRename}
                    onDelete={handleFolderDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Files Section */}
          <div>
            <h2 className="text-lg font-heading text-foreground mb-4">Files</h2>
            
            {filteredFiles?.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                  <Icon name="FileX" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No files found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || Object.keys(filters)?.length > 0
                    ? 'Try adjusting your search or filters' :'Upload your first file to get started'
                  }
                </p>
                {!searchQuery && Object.keys(filters)?.length === 0 && (
                  <Button variant="outline" asChild>
                    <Link to="/create-project">
                      <Icon name="Plus" size={16} />
                      Create Project
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className={
                viewMode === 'grid' ?'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' :'space-y-2'
              }>
                {filteredFiles?.map((file) => (
                  <FileCard
                    key={file?.id}
                    file={file}
                    isSelected={selectedFiles?.includes(file?.id)}
                    onSelect={handleFileSelect}
                    onPreview={handleFilePreview}
                    onDownload={handleFileDownload}
                    onDelete={handleFileDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onDownload={handleFileDownload}
        onDelete={handleFileDelete}
      />
      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedFiles?.length}
        onDownloadSelected={handleBulkDownload}
        onDeleteSelected={handleBulkDelete}
        onMoveSelected={handleBulkMove}
        onClearSelection={() => setSelectedFiles([])}
      />
    </div>
  );
};

export default DigitalVault;